import { Box, Button, Stack } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useApplication } from '../../../../context/ApplicationContext';
import { constructHierarchyArray, extractIds } from './TreeNavigatrionHelper';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import { get } from 'lodash';
import { useLocation } from 'react-router-dom';
import { APIS } from '../../../../lib/constants/apis';
import MuiDialog from '../../mui-dialog/MuiDialog';

import { ANTRAGS_FORMULAREN, ANTRAGS_STATUS, ART_DES_ANTRAGS } from 'src/lib/constants/constant';
import Schema from '../../../../lib/paths/antrag_FULL_Paths.json';
import { GetFieldErrors } from '../../../../lib/validation/ApplicationValidator';
const P = Schema.attributes;

type TreeViewNavigationProps = {
	treeStructure: any;
	currentNode: string[];
	handleNavigation: (nodeIds: string[], buttonClick?: string) => void;
	hasCurrentContentError: () => boolean;
	validateCompleteForm: () => void;
	onStatusCapture?: (status: string) => void;
};
const TreeNavigationButtons: FC<TreeViewNavigationProps> = ({
	treeStructure,
	currentNode,
	handleNavigation,
	hasCurrentContentError,
	validateCompleteForm,
	onStatusCapture,
}) => {
	const location = useLocation();
	const { application, saveApplication, readOnly } = useApplication();
	const [previousNodes, setPreviousNodes] = useState<string[]>(['1']);
	const [nextNodes, setNextNodes] = useState<string[]>(['1']);
	const [hasError, setHasError] = useState(false);

	const [submitDialogOpen, setSubmitDialogOpen] = useState<boolean>(false);

	const showSubmitForBescheid = useMemo(() => {
		const status = get(application, P.Antragstatus_Antragstatus.path, '');
		const RechtbefehlDatum = get(application, P.Angaben_Zum_Rechtsbehelf_Einreichdatum.path, null);

		if (status === ANTRAGS_STATUS.FoerderbescheidUebermittelt && RechtbefehlDatum === null) {
			return true;
		}
		return false;
	}, [application]);

	const handleSubmitApp = async () => {
		setSubmitDialogOpen(false);
		// Capture the current status before submit
		const currentStatus = get(application, P.Antragstatus_Antragstatus.path, '');
		if (onStatusCapture) {
			onStatusCapture(currentStatus);
		}
		saveApplication(true);
	};

	const DialogTitle = useMemo(() => {
		const isFormantrag = get(application, P.Allgemein_Art_Des_Antrags.path, '') === ART_DES_ANTRAGS.Formantrag;
		switch (get(location, 'state.api')) {
			case APIS.Antrag:
				if (showSubmitForBescheid) return 'Eingangsbestätigung';
				if (get(application, P.Allgemein_Typ.path) === 'Skizze') return 'Skizze einreichen';
				if (isFormantrag) return 'Formantrag einreichen';
				return 'Antrag einreichen';
			case APIS.Mittelabruf:
				return 'Mittelabruf einreichen';
			case APIS.Verwendungsnachweis:
				return 'Verwendungsnachweis einreichen';
			case APIS.Zwischenbericht:
				return 'Zwischenbericht einreichen';
			case APIS.Abschlussbericht:
				return 'Abschlussbericht einreichen';
			default:
				return 'Antrag einreichen';
		}
	}, [application, location, showSubmitForBescheid]);
	const DialogContent = useMemo(() => {
		const antragsformular = get(application, P.Allgemein_Antragsformular.path, '');
		const isFormantrag = get(application, P.Allgemein_Art_Des_Antrags.path, '') === ART_DES_ANTRAGS.Formantrag;
		switch (get(location, 'state.api')) {
			case APIS.Antrag:
				if (showSubmitForBescheid) return 'Sind Sie sicher, dass Sie Ihre Eingangsbestätigung einreichen möchten?';
				if (get(application, P.Allgemein_Typ.path) === 'Skizze') return 'Sind Sie sicher, dass Sie die Skizze einreichen möchten?';
				switch (antragsformular) {
					case ANTRAGS_FORMULAREN.StartUpStipendium:
						return `Sind Sie sicher, dass Sie den ${isFormantrag ? 'Formantrag' : 'Antrag'} hochladen möchten?`;
					default:
						return `Sind Sie sicher, dass Sie den ${isFormantrag ? 'Formantrag' : 'Antrag'} einreichen möchten?`;
				}
			case APIS.Mittelabruf:
				return 'Sind Sie sicher, dass Sie den Mittelabruf einreichen möchten?';
			case APIS.Verwendungsnachweis:
				return 'Sind Sie sicher, dass Sie den Verwendungsnachweis einreichen möchten?';
			case APIS.Zwischenbericht:
				return 'Sind Sie sicher, dass Sie den Zwischenbericht einreichen möchten?';
			case APIS.Abschlussbericht:
				return 'Sind Sie sicher, dass Sie den Abschlussbericht einreichen möchten?';
			default:
				return 'Sind Sie sicher, dass Sie den Antrag einreichen möchten?';
		}
	}, [application, location, showSubmitForBescheid]);

	useEffect(() => {
		if (!currentNode || currentNode.length === 0) return;

		const allNodesIdsArr = extractIds(treeStructure);
		const currentNodeStr = currentNode[currentNode.length - 1];
		const currentNodeIndex = allNodesIdsArr.findIndex((ele: any) => ele === currentNodeStr);

		if (currentNodeIndex === -1) return;
		let prevNodeStr = allNodesIdsArr[currentNodeIndex - 1];
		let nextNodeStr = allNodesIdsArr[currentNodeIndex + 1];
		// Update state
		setPreviousNodes(constructHierarchyArray(prevNodeStr));
		setNextNodes(constructHierarchyArray(nextNodeStr));
	}, [treeStructure, currentNode]);

	const hasAnyErrorInForm = (): boolean => {
		const errorList = GetFieldErrors(treeStructure, application, readOnly);
		return errorList.length > 0;
	};

	const einreichen = () => {
		setSubmitDialogOpen(true);
		hasCurrentContentError();
		validateCompleteForm();
		setHasError(hasAnyErrorInForm());
	};

	return (
		<>
			<MuiDialog
				open={submitDialogOpen}
				onClose={() => setSubmitDialogOpen(false)}
				onConfirm={() => handleSubmitApp()}
				title={hasError ? 'Fehler im Formular' : DialogTitle}
				content={hasError ? 'Es befinden sich noch einige Fehler im Formular' : DialogContent}
				hasError={hasError}
			/>
			<Box className='detailNavigationButtons'>
				<Stack
					// sx={{ mt: '2rem' }}
					direction={'row'}
					justifyContent={'space-between'}
				>
					<Button
						variant='secondaryButton'
						disabled={previousNodes[0] === undefined}
						onClick={() => {
							handleNavigation([], 'previous');
						}}
						startIcon={<ArrowBackIcon />}
						sx={{ pointerEvents: 'initial' }}
					>
						Zurück
					</Button>

					{nextNodes[0] !== undefined ?
						<Button
							variant='contained'
							onClick={() => handleNavigation([], 'next')}
							endIcon={<ArrowForwardIcon />}
							sx={{ pointerEvents: 'initial' }}
						>
							Weiter
						</Button>
					:	<>
							{!readOnly || showSubmitForBescheid ?
								<Button endIcon={<SendIcon />} onClick={() => einreichen()} sx={{ pointerEvents: 'initial' }}>
									Einreichen
								</Button>
							:	<></>}
						</>
					}
				</Stack>
			</Box>
		</>
	);
};
export default TreeNavigationButtons;
